import { useEffect } from 'react';

import { Checkmark, Copy } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import clsx from 'clsx';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { SeatInvitation } from 'src/api/endpoints/storeApi.types';
import { Button, Tooltip } from 'src/components';
import { formatRemainingTime } from 'src/utils/formatRemainingTime';
import { useCopyToClipboard } from 'src/utils/useCopyToClipboard';

export const getInvitationStatusText = (
  invitation?: SeatInvitation
): {
  mainStatus?: string;
  mainStatusShort?: string;
  subStatus?: string;
  subStatusShort?: string;
} => {
  if (!invitation) return {};

  if (invitation.isDeclined) {
    return {
      mainStatus: 'Invitation declined',
      mainStatusShort: 'Declined',
    };
  }

  if (invitation.isExpired) {
    return {
      mainStatus: 'Invitation expired',
      mainStatusShort: 'Expired',
    };
  }

  const expirationTime = formatRemainingTime(invitation.expiresAt);

  return {
    mainStatus: 'Invitation pending',
    mainStatusShort: 'Pending',
    subStatus: `Expires in ${expirationTime}`,
    subStatusShort: `(${expirationTime})`,
  };
};

export const InvitationStatus = ({
  invitation,
}: {
  invitation?: SeatInvitation;
}) => {
  const copyToClipboard = useCopyToClipboard();

  useEffect(() => {
    if (copyToClipboard.isSuccess) {
      setTimeout(() => {
        copyToClipboard.reset();
      }, 2000);
    }
  }, [copyToClipboard.isSuccess]);

  if (!invitation) return null;

  if (invitation.isDeclined)
    return (
      <Typography variant="body-sm">
        <span className="hidden italic lm:inline">Invitation declined</span>
        <span className="italic lm:hidden">Declined</span>
      </Typography>
    );

  if (invitation.isExpired)
    return (
      <Typography variant="body-sm">
        <span className="hidden italic lm:inline">Invitation expired</span>
        <span className="italic lm:hidden">Expired</span>
      </Typography>
    );

  const expirationTime = formatRemainingTime(invitation.expiresAt);
  const status = getInvitationStatusText(invitation);

  return (
    <>
      <div className="lm:hidden">
        <Typography
          variant="body-sm"
          className="my-0 whitespace-nowrap italic lm:hidden"
        >
          {status.mainStatusShort}
          <span className="ls:hidden">
            {' '}
            ⋅ Invitation expires in {expirationTime}
          </span>
        </Typography>
      </div>

      <div className="hidden items-center gap-12 lm:flex">
        <div className="flex flex-col gap-8 ls:gap-0">
          <Typography
            variant="body-sm"
            className="my-0 whitespace-nowrap italic"
          >
            {status.mainStatus}
          </Typography>
          <Typography
            variant="body-sm"
            className="my-0 ls:inline ls:interface-xs"
          >
            <span className="hidden text-gray-500 ls:inline">
              {status.subStatus}
            </span>

            <span className="ls:hidden">{status.subStatusShort}</span>
          </Typography>
        </div>

        <Tooltip title="Copy link">
          <Button
            aria-label="Copy link"
            variant="tertiary-neutral"
            size="small"
            disabled={invitation.isDeclined || invitation.isExpired}
            className={clsx('mr-2 hidden ls:flex', {
              invisible: invitation.isDeclined || invitation.isExpired,
            })}
            onClick={() => {
              copyToClipboard.mutate(invitation.invitationLink);
            }}
            data-cy="copy-link-button"
            analytics={{
              text: 'Copy link',
              location: ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
              action: 'copy invitation link',
            }}
          >
            {copyToClipboard.isSuccess ? (
              <Checkmark className="text-feedback-green-500" />
            ) : (
              <Copy />
            )}
          </Button>
        </Tooltip>
      </div>
    </>
  );
};
